import React from 'react';
import classNames from 'classnames';
import { Button as AntDButton, ButtonProps as AntDButtonProps } from 'antd';

import './index.less';

export type Colors = 'default' | 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'error';

interface Props extends Omit<AntDButtonProps, 'type' | 'ghost'> {
  variant?: 'outlined' | 'text' | 'contained' | 'tertiary';
  color?: Colors;
  size?: AntDButtonProps['size'];
  type?: 'link';
  noPadding?: boolean;
}

export type { Props as ButtonProps };

const getButtonColor = (color: Props['color'], variant: Props['variant'], danger = false) => {
  if (danger) {
    return 'error';
  }
  if (variant === 'contained' && !color) {
    return 'default';
  }
  if (variant === 'contained' && !color) {
    return 'primary';
  }
  if (color === 'default') {
    return 'gray';
  }

  if (variant === 'text') {
    return color || 'gray';
  }

  return color || 'gray';
};

const getButtonPropsFromVariant = (
  variant: Props['variant']
): Pick<AntDButtonProps, 'type' | 'ghost'> => {
  switch (variant) {
    case 'text':
      return {
        type: 'text',
      };
    case 'outlined':
      return {
        type: 'primary',
        ghost: true,
      };
    case 'contained':
      return {
        type: 'primary',
        ghost: false,
      };
    default:
      return {};
  }
};

export default function Button({
  icon,
  color,
  type: buttonType,
  variant = 'outlined',
  danger = false,
  disabled = false,
  size = 'middle',
  className = '',
  ...props
}: Props) {
  const { type, ghost } = getButtonPropsFromVariant(variant);
  const buttonColor = getButtonColor(color, variant, danger);
  const [childrenCount, setChildrenCount] = React.useState(() => {
    const numChildrem = Array.isArray(props.children) ? props.children.length : 1;

    return numChildrem + (icon ? 1 : 0);
  });
  return (
    <AntDButton
      icon={icon}
      disabled={disabled}
      size={size}
      danger={buttonColor === 'error'}
      ref={node => {
        node && setChildrenCount(node.childElementCount - (icon ? 1 : 0));
      }}
      type={buttonType || type}
      ghost={ghost}
      className={classNames(
        `sl-button sl-button-${buttonColor} sl-button-${size} focus:tw-outline-4 tw-inline-flex tw-gap-2 tw-flex-nowrap tw-items-center tw-justify-center`,
        className,
        {
          // padding
          'tw-px-4':
            (size === 'large' && childrenCount === 0) ||
            (size === 'small' && childrenCount > 0 && variant !== 'text'),
          'tw-px-3': size === 'middle' && childrenCount === 0,
          'tw-px-2': size === 'small' && childrenCount === 0,
          'tw-py-3': size === 'large' && childrenCount > 0,
          'tw-px-8': size === 'large' && childrenCount > 0 && variant !== 'text',
          'tw-py-2': size === 'middle' && childrenCount > 0,
          'tw-px-6': size === 'middle' && childrenCount > 0 && variant !== 'text',
          'tw-py-1': size === 'small' && childrenCount > 0,

          // font
          'tw-text-button-2 tw-font-medium': buttonType === 'link' && size !== 'large',

          // disabled
          'tw-border-none tw-opacity-40 tw-pointer-events-none': disabled,
          // focus outline
          'focus:tw-outline-gray-200': buttonColor === 'gray',
          'focus:tw-outline-secondary-200': buttonColor === 'secondary',
          'focus:tw-outline-success-200': buttonColor === 'success',
          'focus:tw-outline-info-200': buttonColor === 'info',
          'focus:tw-outline-warning-200': buttonColor === 'warning',
          'focus:tw-outline-error-200': buttonColor === 'error',
          'focus:tw-outline-primary-200': buttonColor === 'primary',
          // text
          'tw-text-gray-700': variant === 'text' && buttonColor === 'gray',
          'tw-text-primary-600': variant === 'text' && buttonColor === 'primary',
          'tw-text-secondary': variant === 'text' && buttonColor === 'secondary',
          'tw-text-success': variant === 'text' && buttonColor === 'success',
          'tw-text-info': variant === 'text' && buttonColor === 'info',
          'tw-text-warning': variant === 'text' && buttonColor === 'warning',
          'tw-text-error': variant === 'text' && buttonColor === 'error',
          // link hover effect
          'hover:tw-underline hover:tw-bg-none hover:tw-bg-transparent':
            buttonType === 'link' && variant === 'text',
          // text hover effect
          'hover:tw-text-gray-600 active:tw-text-gray-700 active:tw-bg-gray-100':
            buttonColor === 'gray' && variant === 'text' && buttonType !== 'link',
          'hover:tw-bg-primary-100 hover:tw-text-primary-600 active:tw-text-primary-700 active:tw-bg-primary-200':
            buttonColor === 'primary' && variant === 'text' && buttonType !== 'link',
          'hover:tw-text-secondary-600 active:tw-text-secondary-700 active:tw-bg-secondary-200':
            buttonColor === 'secondary' && variant === 'text' && buttonType !== 'link',
          'hover:tw-text-success-600 active:tw-text-success-700 active:tw-bg-success-200':
            buttonColor === 'success' && variant === 'text' && buttonType !== 'link',
          'hover:tw-text-info-600 active:tw-text-info-700 active:tw-bg-info-200':
            buttonColor === 'info' && variant === 'text' && buttonType !== 'link',
          'hover:tw-text-warning-600 active:tw-text-warning-700 active:tw-bg-warning-200':
            buttonColor === 'warning' && variant === 'text' && buttonType !== 'link',
          'hover:tw-text-error-600 active:tw-text-error-700 active:tw-bg-error-200':
            buttonColor === 'error' && variant === 'text' && buttonType !== 'link',
          // solid
          'tw-text-gray-600 tw-bg-gray-100 tw-border-gray-100 hover:tw-border-gray-200 active:tw-border-gray-300 hover:tw-bg-gray-200 active:tw-bg-gray-300':
            buttonColor === 'gray' && variant === 'contained',
          'tw-border-primary tw-bg-primary hover:tw-bg-primary-600 active:tw-bg-primary-700 hover:tw-text-white tw-text-white':
            buttonColor === 'primary' && variant === 'contained',
          'tw-border-secondary tw-bg-secondary hover:tw-bg-secondary-600 active:tw-bg-secondary-700 hover:tw-text-white':
            buttonColor === 'secondary' && variant === 'contained',
          'tw-border-success tw-bg-success hover:tw-bg-success-600 active:tw-bg-success-700 hover:tw-text-white':
            buttonColor === 'success' && variant === 'contained',
          'tw-border-info tw-bg-info hover:tw-bg-info-600 active:tw-bg-info-700 hover:tw-text-white':
            buttonColor === 'info' && variant === 'contained',
          'tw-border-warning tw-bg-warning hover:tw-bg-warning-600 active:tw-bg-warning-700 hover:tw-text-white':
            buttonColor === 'warning' && variant === 'contained',
          'tw-border-error tw-bg-error hover:tw-bg-error-600 active:tw-bg-error-700 hover:tw-text-white':
            buttonColor === 'error' && variant === 'contained',
          // lighter
          'tw-border-0': variant === 'tertiary',
          'tw-text-gray-700 tw-bg-gray-100 hover:tw-text-gray-600 hover:tw-bg-gray-200 active:tw-text-gray-700 active:tw-bg-gray-300':
            buttonColor === 'gray' && variant === 'tertiary',
          'tw-text-primary tw-bg-primary-100 hover:tw-text-primary-600 hover:tw-bg-primary-200 active:tw-text-primary-300 active:tw-bg-primary-300':
            buttonColor === 'primary' && variant === 'tertiary',
          'tw-text-secondary tw-bg-secondary-100 hover:tw-text-secondary-600 hover:tw-bg-secondary-200 active:tw-text-secondary-300 active:tw-bg-secondary-300':
            buttonColor === 'secondary' && variant === 'tertiary',
          'tw-text-success tw-bg-success-100 hover:tw-text-success-600 hover:tw-bg-success-200 active:tw-text-success-300 active:tw-bg-success-300':
            buttonColor === 'success' && variant === 'tertiary',
          'tw-text-info tw-bg-info-100 hover:tw-text-info-600 hover:tw-bg-info-200 active:tw-text-info-300 active:tw-bg-info-300':
            buttonColor === 'info' && variant === 'tertiary',
          'tw-text-warning tw-bg-warning-100 hover:tw-text-warning-600 hover:tw-bg-warning-200 active:tw-text-warning-300 active:tw-bg-warning-300':
            buttonColor === 'warning' && variant === 'tertiary',
          'tw-text-error tw-bg-error-100 hover:tw-text-error-600 hover:tw-bg-error-200 active:tw-text-error-300 active:tw-bg-error-300':
            buttonColor === 'error' && variant === 'tertiary',
          // ghost (outlined)
          'tw-bg-white': variant === 'outlined',
          'tw-text-gray-700 tw-border-gray-200 hover:tw-text-gray-600 active:tw-text-gray-700 hover:tw-border-gray-300 active:tw-border-gray-300':
            buttonColor === 'gray' && variant === 'outlined',
          'tw-text-primary tw-border-primary hover:tw-text-primary-600 active:tw-text-primary-700 hover:tw-border-primary-600 active:tw-border-primary-700':
            buttonColor === 'primary' && variant === 'outlined',
          'tw-text-success tw-border-success hover:tw-text-success-600 active:tw-text-success-700 hover:tw-border-success-600 active:tw-border-success-700':
            buttonColor === 'success' && variant === 'outlined',
          'tw-text-info tw-border-info hover:tw-text-info-600 active:tw-text-info-700 hover:tw-border-info-600 active:tw-border-info-700':
            buttonColor === 'info' && variant === 'outlined',
          'tw-text-warning tw-border-warning hover:tw-text-warning-600 active:tw-text-warning-700 hover:tw-border-warning-600 active:tw-border-warning-700':
            buttonColor === 'warning' && variant === 'outlined',
          'tw-text-error tw-border-error hover:tw-text-error-600 active:tw-text-error-700 hover:tw-border-error-600 active:tw-border-error-700':
            buttonColor === 'error' && variant === 'outlined',
        }
      )}
      {...getButtonPropsFromVariant(variant)}
      {...props}
    />
  );
}
